import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { ResponsiveContainer, ResponsiveHeader, ResponsiveContent, ResponsiveFooter } from '../ui/ResponsiveLayout';
import { NewChildIcon, NewParentsIcon, NewSiblingsIcon, NewSpousesIcon, NewOtherDependentsIcon, DocumentsIcon } from '../ui/Icons';
import Modal from '../ui/Modal';
import FileUpload from '../ui/FileUpload';
import { documentService } from '../../services/documentService';
import type { AppDocument } from '../../types/document';

interface Dependant {
  id: string;
  name: string;
  relationship: 'child' | 'parent' | 'sibling' | 'spouse' | 'other' | '';
  age: string;
  specialNeeds: string;
  medicalConditions: string;
  medications: string;
  preferredGuardian: string;
  guardianContact: string;
  emergencyContact: string;
  emergencyContactPhone: string;
  careInstructions: string;
  documents: AppDocument[];
}

interface PetsAndDependantsData {
  pets: unknown[];
  dependants: Dependant[];
}

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  return documentService.normalizeMany(docs);
};

const dependentCategories = [
  {
    key: 'child' as const,
    label: 'Children',
    Icon: NewChildIcon,
    description: 'Add your dependent children and their care needs'
  },
  {
    key: 'parent' as const,
    label: 'Parents',
    Icon: NewParentsIcon,
    description: 'Add your dependent parents and their care needs'
  },
  {
    key: 'sibling' as const,
    label: 'Siblings',
    Icon: NewSiblingsIcon,
    description: 'Add your dependent siblings and their care needs'
  },
  {
    key: 'spouse' as const,
    label: 'Spouse',
    Icon: NewSpousesIcon,
    description: 'Add your spouse if they require special care'
  },
  {
    key: 'other' as const,
    label: 'Other\nDependents',
    Icon: NewOtherDependentsIcon,
    description: 'Add any other dependent family members'
  }
];

const Frame249: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress } = useSessionPersistence();

  const [data, setData] = useState<PetsAndDependantsData>({
    pets: [],
    dependants: []
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [selectedType, setSelectedType] = useState<Dependant['relationship']>('');
  const [editingItemId, setEditingItemId] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const [specialNeeds, setSpecialNeeds] = useState('');
  const [primaryContact, setPrimaryContact] = useState('');
  const [primaryContactPhone, setPrimaryContactPhone] = useState('');
  const [secondaryContact, setSecondaryContact] = useState('');
  const [secondaryContactPhone, setSecondaryContactPhone] = useState('');
  const [medicalInfo, setMedicalInfo] = useState('');
  const [medications, setMedications] = useState('');
  const [careInstructions, setCareInstructions] = useState('');
  const [documents, setDocuments] = useState<AppDocument[]>([]);
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);

  const normalizeData = (value: PetsAndDependantsData): PetsAndDependantsData => {
    return {
      pets: value?.pets || [],
      dependants: (value?.dependants || []).map((dependant) => ({
        ...dependant,
        documents: normalizeDocuments(dependant.documents || []),
      })),
    };
  };

  const openDocument = (doc: AppDocument) => {
    if (!doc.id) return;
    documentService.open(doc.id);
  };

  const downloadDocument = async (doc: AppDocument) => {
    if (!doc.id) return;

    try {
      await documentService.download(doc.id, doc.name);
    } catch (error) {
      console.error('[Frame249] Failed to download document:', error);
      alert('Failed to download document.');
    }
  };

  const saveToLocalStorage = (dataToSave: PetsAndDependantsData) => {
    try {
      const normalized = normalizeData(dataToSave);

      const saveData = {
        answers: {
          petsAndDependants: normalized
        },
        lastUpdated: new Date().toISOString()
      };

      localStorage.setItem('pets-dependants-backup', JSON.stringify(normalized));
      localStorage.setItem('pets-dependants-data-backup', JSON.stringify(saveData));

      return true;
    } catch (error) {
      console.error('[Frame249] Direct save failed:', error);
      return false;
    }
  };

  useEffect(() => {
    if (session?.taskProgress?.pets?.answers) {
      const savedData = session.taskProgress.pets.answers.petsAndDependants;
      if (savedData) {
        console.log('[Frame249] Loading from session:', savedData);
        setData(normalizeData(savedData));
        return;
      }
    }

    try {
      const backupData = localStorage.getItem('pets-dependants-backup');
      if (backupData) {
        const parsed = JSON.parse(backupData);
        if (parsed) {
          console.log('[Frame249] Loading from localStorage backup:', parsed);
          setData(normalizeData(parsed));
        }
      }
    } catch (error) {
      console.error('[Frame249] Failed to load from backup:', error);
    }
  }, [session?.taskProgress?.pets]);

  const updateData = (newData: PetsAndDependantsData) => {
    const normalized = normalizeData(newData);
    setData(normalized);

    saveToLocalStorage(normalized);

    updateTaskProgress('pets', { petsAndDependants: normalized }).catch(error => {
      console.error('[Frame249] Session save failed:', error);
    });
  };

  const resetModal = () => {
    setName('');
    setAge('');
    setSpecialNeeds('');
    setPrimaryContact('');
    setPrimaryContactPhone('');
    setSecondaryContact('');
    setSecondaryContactPhone('');
    setMedicalInfo('');
    setMedications('');
    setCareInstructions('');
    setDocuments([]);
    setEditingItemId(null);
    setIsUploadingDocuments(false);
  };

  const openDependentModal = (relationship: Dependant['relationship']) => {
    resetModal();
    setSelectedType(relationship);
    setModalTitle(`Add ${relationship.charAt(0).toUpperCase() + relationship.slice(1)}`);
    setModalOpen(true);
  };

  const handleOk = () => {
    if (!name.trim()) {
      alert('Name is required');
      return;
    }

    const newDependent: Dependant = {
      id: editingItemId || Date.now().toString(),
      name: name.trim(),
      relationship: selectedType as Dependant['relationship'],
      age: age.trim(),
      specialNeeds: specialNeeds.trim(),
      medicalConditions: medicalInfo.trim(),
      medications: medications.trim(),
      preferredGuardian: primaryContact.trim(),
      guardianContact: primaryContactPhone.trim(),
      emergencyContact: secondaryContact.trim(),
      emergencyContactPhone: secondaryContactPhone.trim(),
      careInstructions: careInstructions.trim(),
      documents: normalizeDocuments(documents)
    };

    const updatedDependents = editingItemId
      ? data.dependants.map(d => d.id === editingItemId ? newDependent : d)
      : [...data.dependants, newDependent];

    updateData({
      ...data,
      dependants: updatedDependents
    });

    setModalOpen(false);
  };

  const handleCancel = () => setModalOpen(false);

  const handleEdit = (item: Dependant) => {
    setSelectedType(item.relationship);
    setName(item.name);
    setAge(item.age);
    setSpecialNeeds(item.specialNeeds);
    setPrimaryContact(item.preferredGuardian);
    setPrimaryContactPhone(item.guardianContact);
    setSecondaryContact(item.emergencyContact);
    setSecondaryContactPhone(item.emergencyContactPhone);
    setMedicalInfo(item.medicalConditions);
    setMedications(item.medications);
    setCareInstructions(item.careInstructions);
    setDocuments(normalizeDocuments(item.documents || []));
    setEditingItemId(item.id);
    setModalTitle(`Edit ${item.name}`);
    setModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this item?')) return;

    const dependantToDelete = data.dependants.find(d => d.id === id);
    const attachedDocs = normalizeDocuments(dependantToDelete?.documents || []);

    for (const doc of attachedDocs) {
      try {
        await documentService.delete(doc.id);
      } catch (error) {
        console.error('[Frame249] Failed to delete document while deleting dependant:', error);
      }
    }

    updateData({
      ...data,
      dependants: data.dependants.filter(d => d.id !== id)
    });
  };

  const handleRemoveAttachedDocument = async (dependantId: string, documentIndex: number) => {
    const dependant = data.dependants.find(d => d.id === dependantId);
    const docToRemove = dependant?.documents?.[documentIndex];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame249] Failed to delete saved document:', error);
      alert('Failed to delete document.');
      return;
    }

    const updatedDependants = data.dependants.map((dependant) => {
      if (dependant.id !== dependantId) return dependant;
      return {
        ...dependant,
        documents: normalizeDocuments(dependant.documents || []).filter((_, index) => index !== documentIndex),
      };
    });

    updateData({
      ...data,
      dependants: updatedDependants
    });
  };

  const removeDocumentFromUploadModal = async (index: number) => {
    const docToRemove = documents[index];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame249] Failed to delete unsaved document:', error);
      alert('Failed to delete document.');
      return;
    }

    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleNext = async () => {
    try {
      if (!saveToLocalStorage(data)) {
        throw new Error('Failed to save to local storage');
      }

      await updateTaskProgress('pets', { petsAndDependants: normalizeData(data) });
      navigate('/frame-243');
    } catch (error) {
      console.error('[Frame249] Save failed:', error);

      const message = 'Failed to save your changes. Please try again.';
      if (confirm(message + '\n\nClick OK to try again or Cancel to discard changes.')) {
        handleNext();
      } else {
        navigate('/frame-243');
      }
    }
  };

  const handleBack = async () => {
    try {
      saveToLocalStorage(data);
      await updateTaskProgress('pets', { petsAndDependants: normalizeData(data) });
      navigate('/frame-242');
    } catch {
      navigate('/frame-242');
    }
  };

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <ResponsiveHeader title="Add Your Dependents" />

        <div className="max-w-4xl">
          <div className="text-stone-800 text-lg font-medium font-sans mb-8 leading-relaxed">
            Add any dependents who rely on you for care, including children, parents, siblings, or others.
          </div>

          <div className="mb-12">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {dependentCategories.map((category) => (
                <button
                  key={category.key}
                  onClick={() => openDependentModal(category.key)}
                  className="group w-full h-[140px] bg-white rounded-lg border border-neutral-200 flex flex-row items-center gap-4 p-4 hover:ring-2 hover:ring-cyan-700 transition-all text-left"
                >
                  <div className="flex items-center gap-4">
                    <category.Icon className="w-8 h-8 text-stone-800 group-hover:text-cyan-700 transition-colors flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-stone-800 whitespace-pre-line leading-tight">
                        {category.label}
                      </h3>
                      <p className="text-sm text-stone-600 mt-1">{category.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Dependent List */}
            {data.dependants.length > 0 && (
              <div className="mt-8">
                <h2 className="text-lg font-medium text-stone-800 mb-4">
                  Your Dependents ({data.dependants.length})
                </h2>
                <div className={`space-y-4 ${data.dependants.length >= 3 ? 'max-h-56 min-h-0 overflow-y-auto' : ''}`}>
                  {data.dependants.map((dependent) => {
                    const category = dependentCategories.find((c) => c.key === dependent.relationship);
                    const DepIcon = category?.Icon || NewOtherDependentsIcon;
                    const metadataParts = [
                      dependent.relationship
                        ? dependent.relationship.charAt(0).toUpperCase() + dependent.relationship.slice(1)
                        : null,
                      dependent.age || null,
                      dependent.preferredGuardian ? `Guardian: ${dependent.preferredGuardian}` : null,
                    ].filter(Boolean);

                    const attachedDocs = normalizeDocuments(dependent.documents || []);

                    return (
                      <div
                        key={dependent.id}
                        className="bg-white rounded-lg border border-neutral-200 p-4"
                      >
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                          <div className="flex flex-col gap-2 min-w-0 flex-1 sm:flex-row sm:items-start sm:gap-4">
                            <DepIcon className="w-6 h-6 text-cyan-700 flex-shrink-0" />

                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-stone-800 break-words">{dependent.name}</div>

                              {metadataParts.length > 0 && (
                                <div className="text-sm text-stone-500 mt-1 break-words">
                                  {metadataParts.join(' • ')}
                                </div>
                              )}

                              {dependent.specialNeeds && (
                                <div className="text-sm text-stone-600 mt-2 break-words">
                                  {dependent.specialNeeds}
                                </div>
                              )}

                              {dependent.medicalConditions && (
                                <div className="text-sm text-stone-600 mt-2 break-words">
                                  {dependent.medicalConditions}
                                </div>
                              )}

                              {dependent.careInstructions && (
                                <div className="text-sm text-stone-600 mt-2 break-words">
                                  {dependent.careInstructions}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2 sm:flex-nowrap sm:shrink-0">
                            <button
                              onClick={() => handleEdit(dependent)}
                              className="text-cyan-600 hover:text-cyan-700 px-3 py-1 rounded border border-cyan-200 hover:border-cyan-300"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(dependent.id)}
                              className="text-red-600 hover:text-red-700 px-3 py-1 rounded border border-red-200 hover:border-red-300"
                            >
                              Delete
                            </button>
                          </div>
                        </div>

                        {attachedDocs.length > 0 && (
                          <div className="mt-4 space-y-2">
                            {attachedDocs.map((doc, docIndex) => (
                              <div
                                key={`${dependent.id}-${doc.id}-${docIndex}`}
                                className="flex flex-col gap-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                              >
                                <button
                                  type="button"
                                  onClick={() => openDocument(doc)}
                                  className="flex items-start gap-2 min-w-0 w-full text-left sm:flex-1"
                                >
                                  <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
                                  <span
                                    className="text-sm font-medium text-stone-800 truncate min-w-0"
                                    title={doc.name}
                                  >
                                    {doc.name}
                                  </span>
                                </button>

                                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => downloadDocument(doc)}
                                    className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                                  >
                                    Download
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => handleRemoveAttachedDocument(dependent.id, docIndex)}
                                    className="text-red-500 hover:text-red-700 text-xs shrink-0"
                                    title="Remove document"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </ResponsiveContent>

      {/* Add/Edit Modal */}
      <Modal title={modalTitle} open={modalOpen} onOk={handleOk} onCancel={handleCancel} okText={editingItemId ? 'Save Changes' : 'Save'}>
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-cyan-500"
              placeholder="Enter dependent's name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
            <input
              type="text"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-cyan-500"
              placeholder="Age"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Special Needs</label>
            <textarea
              value={specialNeeds}
              onChange={(e) => setSpecialNeeds(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-cyan-500"
              rows={3}
              placeholder="Any special needs or requirements"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Medical Conditions</label>
            <textarea
              value={medicalInfo}
              onChange={(e) => setMedicalInfo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-cyan-500"
              rows={3}
              placeholder="List any medical conditions"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Medications</label>
            <textarea
              value={medications}
              onChange={(e) => setMedications(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-cyan-500"
              rows={3}
              placeholder="List current medications and dosages"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Guardian</label>
            <input
              type="text"
              value={primaryContact}
              onChange={(e) => setPrimaryContact(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-cyan-500"
              placeholder="Guardian name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Guardian Contact</label>
            <input
              type="tel"
              value={primaryContactPhone}
              onChange={(e) => setPrimaryContactPhone(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-cyan-500"
              placeholder="Phone number"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact</label>
            <input
              type="text"
              value={secondaryContact}
              onChange={(e) => setSecondaryContact(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-cyan-500"
              placeholder="Name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact Phone</label>
            <input
              type="tel"
              value={secondaryContactPhone}
              onChange={(e) => setSecondaryContactPhone(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-cyan-500"
              placeholder="Phone number"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Care Instructions</label>
            <textarea
              value={careInstructions}
              onChange={(e) => setCareInstructions(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-cyan-500"
              rows={4}
              placeholder="Detailed care instructions and preferences"
            />
          </div>

          {/* Upload Documents */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Upload Documents (optional)
            </label>
            <FileUpload
              onUpload={async (files: File[]) => {
                if (!files.length) return;

                setIsUploadingDocuments(true);

                try {
                  const uploadedDocs = await Promise.all(
                    files.map((file) => documentService.upload(file))
                  );
                  setDocuments((prev) => [...prev, ...uploadedDocs]);
                } catch (error) {
                  console.error('[Frame249] Failed to upload document(s):', error);
                  alert('Failed to upload one or more documents.');
                } finally {
                  setIsUploadingDocuments(false);
                }
              }}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              multiple
            />

            {isUploadingDocuments && (
              <div className="mt-3 text-sm text-stone-500">
                Uploading documents...
              </div>
            )}

            {documents.length > 0 && (
              <ul className="mt-4 space-y-2">
                {documents.map((doc, index) => (
                  <li
                    key={`${doc.id}-${index}`}
                    className="flex flex-col sm:flex-row sm:items-center justify-between text-sm text-gray-600 bg-gray-50 p-2 rounded border border-gray-100 gap-3"
                  >
                    <button
                      type="button"
                      onClick={() => openDocument(doc)}
                      className="flex items-start gap-2 min-w-0 w-full sm:flex-1 text-left"
                    >
                      <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
                      <span className="break-words whitespace-normal truncate mr-2 font-medium text-stone-800">
                        {doc.name}
                      </span>
                    </button>

                    <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => downloadDocument(doc)}
                        className="text-cyan-600 hover:text-cyan-800 font-medium text-xs"
                      >
                        Download
                      </button>
                      <button
                        type="button"
                        onClick={() => removeDocumentFromUploadModal(index)}
                        className="text-red-500 hover:text-red-700 font-medium text-xs"
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Modal>

      <ResponsiveFooter
        title="Plan for Your Pets and Dependents"
        subtitle="Add dependents who rely on you for care"
        progressPercent={40}
        onBack={handleBack}
        onNext={handleNext}
        nextText="Next"
        backText="Back"
      />
    </ResponsiveContainer>
  );
};

export default Frame249;